import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Modal, PrimaryButton, SecondaryButton } from '../..';
import { useSdk } from '../../../sdkContext';

import css from './PickupFlowModal.css';

const PickupFlowModal = ({
  id = 'PickupFlowModal',
  isOpen = false,
  onCloseModal,
  onManageDisableScrolling,
  transaction,
  actualOrderedProducts = [],
  targetFlow, // 'pickup' | 'protocol'
  onSuccessPickup = () => { },
  onTransactionUpdated = () => { }
}) => {
  const intl = useIntl();
  const sdk = useSdk();
  const queryClient = useQueryClient();

  const [inputValues, setInputValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [assignedPieceCodes, setAssignedPieceCodes] = useState({});

  // Synchronizacja początkowych kodów z zachowaniem indeksów (slotów)
  useEffect(() => {
    if (transaction?.listings) {
      setAssignedPieceCodes(prev => {
        const newMap = { ...prev };
        
        transaction.listings.forEach(listing => {
          const listingId = listing.id.uuid || listing.id;
          const backendCodes = listing?.meta?.pieceCodes || [];
          
          const currentLocalCodes = prev[listingId] || [];
          const nextCodes = [...currentLocalCodes];
          
          // A. Usuń z lokalnego stanu kody, których nie ma już w backendzie
          for (let i = 0; i < nextCodes.length; i++) {
            if (nextCodes[i] && !backendCodes.includes(nextCodes[i])) {
              nextCodes[i] = undefined;
            }
          }
          
          // B. Dodaj kody z backendu w pierwsze wolne miejsca, jeśli ich jeszcze nie mamy
          backendCodes.forEach(code => {
            if (!nextCodes.includes(code)) {
              const emptyIndex = nextCodes.findIndex(c => !c);
              if (emptyIndex !== -1) {
                nextCodes[emptyIndex] = code;
              } else {
                nextCodes.push(code);
              }
            }
          });
          
          newMap[listingId] = nextCodes;
        });
        
        return newMap;
      });
    }
  }, [transaction]);

  const { mutate: handleAddPiece, isLoading: isAddingPiece } = useMutation({
    mutationFn: ({ code, fieldKey, listingId }) => {
      setFieldErrors(prev => ({ ...prev, [fieldKey]: null }));

      return sdk.newSdk.transactions.update({
        id: transaction.id.uuid || transaction.id,
        addPiece: { code: code.trim() },
        include: ['listings'],
      });
    },
    onSuccess: (response, variables) => {
      const { code, listingId, fieldKey } = variables;
      const slotIndex = parseInt(fieldKey.split('-').pop(), 10);

      setAssignedPieceCodes(prev => {
        const currentList = [...(prev[listingId] || [])];
        const trimmedCode = code.trim();
        
        currentList[slotIndex] = trimmedCode;
        
        return {
          ...prev,
          [listingId]: currentList,
        };
      });

      setInputValues(prev => ({ ...prev, [fieldKey]: '' }));
      onTransactionUpdated(transaction.id.uuid);
    },
    onError: (error, variables) => {
      const rawErrorResponse = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawErrorResponse === 'string') {
        errorMessage = rawErrorResponse.trim();
      } else if (rawErrorResponse?.errors?.[0]?.detail) {
        errorMessage = rawErrorResponse.errors[0].detail;
      } else if (rawErrorResponse?.message) {
        errorMessage = rawErrorResponse.message;
      }

      const finalError =
        errorMessage ||
        intl.formatMessage({
          id: 'PickupFlowModal.addPieceError',
          defaultMessage: 'Nie udało się przypisać kodu. Sprawdź, czy numer ID jest poprawny.',
        });

      if (variables.fieldKey) {
        setFieldErrors(prev => ({ ...prev, [variables.fieldKey]: finalError }));
      }
    },
  });

  const { mutate: handleRemovePiece, isLoading: isRemovingPiece } = useMutation({
    mutationFn: ({ code, fieldKey, listingId }) => {
      if (fieldKey) {
        setFieldErrors(prev => ({ ...prev, [fieldKey]: null }));
      }
      return sdk.newSdk.transactions.update({
        id: transaction.id.uuid || transaction.id,
        removePiece: { code: code.trim() },
        include: ['listings'],
      });
    },
    onSuccess: (response, variables) => {
      const { code, listingId, fieldKey } = variables;
      const slotIndex = parseInt(fieldKey.split('-').pop(), 10);

      setAssignedPieceCodes(prev => {
        const currentList = [...(prev[listingId] || [])];
        
        if (!isNaN(slotIndex)) {
          currentList[slotIndex] = undefined;
        } else {
          const idx = currentList.indexOf(code);
          if (idx > -1) currentList[idx] = undefined;
        }

        return {
          ...prev,
          [listingId]: currentList,
        };
      });
      onTransactionUpdated(transaction.id.uuid);
    },
    onError: (error, variables) => {
      const rawErrorResponse = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawErrorResponse === 'string') {
        errorMessage = rawErrorResponse.trim();
      } else if (rawErrorResponse?.errors?.[0]?.detail) {
        errorMessage = rawErrorResponse.errors[0].detail;
      } else if (rawErrorResponse?.message) {
        errorMessage = rawErrorResponse.message;
      }

      const finalError =
        errorMessage ||
        intl.formatMessage({
          id: 'PickupFlowModal.removePieceError',
          defaultMessage: 'Błąd podczas usuwania kodu sztuki.',
        });

      if (variables.fieldKey) {
        setFieldErrors(prev => ({ ...prev, [variables.fieldKey]: finalError }));
      }
    },
  });

  const isSubmitting = isAddingPiece || isRemovingPiece;

  const totalRequiredPieces = (actualOrderedProducts || []).reduce((sum, product) => {
    const listing = transaction?.listings?.find(
      l => (l.id.uuid || l.id) === product.listingId
    );
    const isTracked = listing?.attributes?.managedPiecesData?.enabled === true;
    return isTracked ? sum + (product.seats || 1) : sum;
  }, 0);

  // Przypisana liczba sztuk z odfiltrowaniem pustych slotów
  const totalAssignedPiecesCount = Object.values(assignedPieceCodes).reduce(
    (sum, codes) => sum + (codes?.filter(Boolean).length || 0),
    0
  );

  const isAllPiecesAssigned =
    totalRequiredPieces > 0 && totalAssignedPiecesCount >= totalRequiredPieces;

  const handleInputChange = (fieldKey, value) => {
    setInputValues(prev => ({ ...prev, [fieldKey]: value }));
    if (fieldErrors[fieldKey]) {
      setFieldErrors(prev => ({ ...prev, [fieldKey]: null }));
    }
  };

  const submitPiece = (fieldKey, listingId) => {
    const code = inputValues[fieldKey];
    if (!code || !code.trim() || isSubmitting) return;

    handleAddPiece({ code: code.trim(), fieldKey, listingId });
  };

  const handleRemoveSubmit = (code, fieldKey, listingId) => {
    handleRemovePiece({ code, fieldKey, listingId });
  };

  const handleFinishAndGoNext = () => {
    queryClient.invalidateQueries(['transaction']);
    onSuccessPickup();
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={() => {
        queryClient.invalidateQueries(['transaction']);
        onCloseModal();
      }}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.container}>
        <h2 className={css.title}>
          <FormattedMessage
            id="PickupFlowModal.title"
            defaultMessage="Wprowadź numery ID wydawanych produktów"
          />
        </h2>

        <div className={css.productList}>
          {actualOrderedProducts && actualOrderedProducts.length > 0 ? (
            actualOrderedProducts.map((product, pIndex) => {
              const listing = transaction?.listings?.find(
                l => (l.id.uuid || l.id) === product.listingId
              );

              const listingId = listing?.id?.uuid || listing?.id;
              const isTracked = listing?.attributes?.managedPiecesData?.enabled === true;
              const quantity = product.seats || listing?.meta?.seats || 1;
              const title = listing?.attributes?.title || '';
              const modelCode = listing?.attributes?.code || '';

              const pieceCodes = assignedPieceCodes[listingId] || [];
              const inputSlots = Array.from({ length: quantity });

              return (
                <div key={product.id || pIndex} className={css.productGroup}>
                  <div className={css.productHeader}>
                    <span className={css.productTitle}>
                      {title} {modelCode ? `(${modelCode})` : ''}
                    </span>
                    <span className={css.quantityBadge}>x{quantity}</span>
                  </div>

                  {!isTracked ? (
                    <div className={css.noTrackingNotice}>
                      <FormattedMessage
                        id="PickupFlowModal.noTracking"
                        defaultMessage="Produkt nie ma nadanego numeru ID"
                      />
                    </div>
                  ) : (
                    <div className={css.inputsContainer}>
                      {inputSlots.map((_, slotIndex) => {
                        const fieldKey = `${product.id || pIndex}-${slotIndex}`;
                        const assignedCode = pieceCodes[slotIndex];
                        const isAssigned = !!assignedCode;
                        const hasError = !!fieldErrors[fieldKey];

                        return (
                          <div key={slotIndex} className={css.fieldWrapper}>
                            <div className={css.inputRow}>
                              {isAssigned ? (
                                <>
                                  <input
                                    type="text"
                                    className={classNames(css.input, css.inputSuccess)}
                                    value={assignedCode}
                                    disabled
                                  />
                                  <SecondaryButton
                                    className={css.removeButton}
                                    onClick={() =>
                                      handleRemoveSubmit(assignedCode, fieldKey, listingId)
                                    }
                                    disabled={isSubmitting}
                                  >
                                    <FormattedMessage
                                      id="PickupFlowModal.remove"
                                      defaultMessage="Usuń"
                                    />
                                  </SecondaryButton>
                                </>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    className={classNames(css.input, {
                                      [css.inputError]: hasError,
                                    })}
                                    placeholder="Zeskanuj naklejkę..."
                                    value={inputValues[fieldKey] || ''}
                                    onChange={e => handleInputChange(fieldKey, e.target.value)}
                                    onBlur={() => submitPiece(fieldKey, listingId)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitPiece(fieldKey, listingId);
                                      }
                                    }}
                                    disabled={isSubmitting}
                                  />
                                  <PrimaryButton
                                    className={css.addButton}
                                    onClick={() => submitPiece(fieldKey, listingId)}
                                    disabled={isSubmitting || !inputValues[fieldKey]?.trim()}
                                  >
                                    <FormattedMessage
                                      id="PickupFlowModal.add"
                                      defaultMessage="Dodaj"
                                    />
                                  </PrimaryButton>
                                </>
                              )}
                            </div>
                            {hasError && (
                              <div className={css.fieldErrorText}>
                                {fieldErrors[fieldKey]}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className={css.emptyList}>
              <FormattedMessage
                id="PickupFlowModal.noProducts"
                defaultMessage="Brak produktów w tym zamówieniu."
              />
            </div>
          )}
        </div>

        <div className={css.actions}>
          <SecondaryButton
            className={css.cancelButton}
            onClick={() => {
              queryClient.invalidateQueries(['transaction']);
              onCloseModal();
            }}
          >
            <FormattedMessage id="PickupFlowModal.cancel" defaultMessage="ANULUJ" />
          </SecondaryButton>
          <PrimaryButton
            className={css.submitButton}
            onClick={handleFinishAndGoNext}
            disabled={!isAllPiecesAssigned || isSubmitting}
            inProgress={isSubmitting}
          >
            {targetFlow === 'pickup' ? (
              <FormattedMessage
                id="PickupFlowModal.confirmPickup"
                defaultMessage="WYDAJ PRODUKTY"
              />
            ) : targetFlow === 'protocol' ? (
              <FormattedMessage
                id="PickupFlowModal.fillProtocol"
                defaultMessage="WYPEŁNIJ PROTOKÓŁ"
              />
            ) : (
              <FormattedMessage
                id="PickupFlowModal.goNext"
                defaultMessage="IDŹ DALEJ"
              />
            )}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default PickupFlowModal;